/* eslint-disable no-template-curly-in-string */


import React, { useState, useRef, memo, useMemo } from 'react';

import styled from 'styled-components';
import { useEditModeManager } from '../../../../../../../common/hooks';
import editModeManager from '../../../../../../../common/utils/EditModeManager';
import { purifyHTML } from '../../../../../../../common/utils';


import { OptionsBlock } from '../Actions';
import { Code, TextColor, Visible } from '../Actions/components/OptionsList';
import EditibleBlock from './components/common/EditableBlock';

import { ActionTypes } from '../../../../types';
import { InputBase, RelativeBlock } from './components/common/StyledEditables';
import useOuterClick from '../../../../hooks/useOuterClick';

const TitleH1Base = styled.h1.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const TitleH2Base = styled.h2.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const TitleH3Base = styled.h3.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const TitleH4Base = styled.h4.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const TitleH5Base = styled.h5.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const ClickableDiv = styled.div`
  opacity: ${(props) => !props.isVisible && '0.25'}
`;















const Title = (props) => {
  const {
    title,
    style,
    computedStyle,
    moduleId,
    rowId,
    columnId,
    isEditable,
    isVisible,
    showRecipientName,
    handleTemplateChange,
    hideVisibleOption
  } = props;
  const {
    text = '',
    tag = 'h1',
    className = ''
  } = title;

  const titleProps = { style, computedStyle, className };

  const inputRef = useRef();
  const componentRef = useRef();
  const editorButtonRef = useRef([]);

  const [canEdit, setShowEdit] = useState(false);
  const [textValue, setTextValue] = useState(text);
  const [isColorPickerVisible, setColorPicker] = useState(false);
  const [isRecipientNameVisible, setRecipientName] = useState(false);

  const componentId = useMemo(() => `title-${moduleId || 'unknown'}-${rowId || 'unknown'}-${columnId || 'unknown'}`, [moduleId, rowId, columnId]);

  const hideEdit = () => {
    editModeManager.clearActiveComponent();
    setShowEdit(false);
  };

  const showEdit = () => {
    editModeManager.setActiveComponent(componentId);
    setShowEdit(true);
  };


  const hideColorPicker = () => setColorPicker(false);
  const hideRecipientNameOption = () => setRecipientName(false);

  const showColorPicker = () => {
    setColorPicker(true);
    hideRecipientNameOption();
  };

  const showRecipientNameOption = () => {
    setRecipientName(true);
    hideColorPicker();
  };

  const handleOuterClick = () => {
    if (!canEdit) {
      return;
    }

    hideEdit();
    hideColorPicker();
    hideRecipientNameOption();
  };

  useOuterClick({ dependencies: [canEdit], callback: handleOuterClick, innerRef: componentRef });

  useEditModeManager({
    componentId,
    callbackToHide: () => {
      setShowEdit(false);
      hideColorPicker();
      hideRecipientNameOption();
    },
    optionalConditionToHide: canEdit || isColorPickerVisible || isRecipientNameVisible,
    dependencies: [canEdit, isColorPickerVisible, isRecipientNameVisible]
  });

  const setRef = (node) => {
    inputRef.current = node;

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const updateTitleText = (inputValue) => {
    setTextValue(inputValue);

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          title: {
            tag,
            className,
            text: inputValue
          }
        }
      } });
  };

  const setIsVisible = () => {
    hideColorPicker();
    hideRecipientNameOption();
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        overrides: {
          isVisible: !isVisible
        }
      } });
  };

  const handlePickerColorChange = ({ hex = '' } = {}) => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        moduleDescriptorValueToSet: {
          style: {
            ...style,
            color: hex.toUpperCase()
          }
        }
      } });
  };

  const addRecipientName = (e, type) => {
    e.stopPropagation();
    if (editorButtonRef.current && editorButtonRef.current[type]) {
      editorButtonRef.current[type].click();
    }
  };

  const handleBlockClick = (e) => {
    e.stopPropagation();

    if (isEditable) {
      showEdit();
    }

    return void 0;
  };

  const setButtonRef = (type, node) => {
    if (node) {
      editorButtonRef.current[type] = node;
    }
  };

  const options = [
  ...(!hideVisibleOption ? [
  {
    action: setIsVisible,
    label:
    <Visible isVisible={isVisible} />

  }] :
  []),
  {
    action: isColorPickerVisible ? hideColorPicker : showColorPicker,
    isActive: isColorPickerVisible,
    label:
    <TextColor active={isColorPickerVisible} style={style} />

  },
  ...(showRecipientName ? [
  {
    action: isRecipientNameVisible ? hideRecipientNameOption : showRecipientNameOption,
    isActive: isRecipientNameVisible,
    label: <Code active={isRecipientNameVisible} />
  }] : [])];


  const replacedText = useMemo(() => {
    if (showRecipientName && text) {
      return text.
      replace(/\$\{recipientName\}/g, '<span style="color: #27BBA1;">&lt;Recipient Name&gt;</span>').
      replace(/\$\{recipientCompany\}/g, '<span style="color: #27BBA1;">&lt;Company Name&gt;</span>').
      replace(/(?:\r\n|\r|\n)/g, '<br/>');
    }

    return text.replace(/(?:\r\n|\r|\n)/g, '<br/>');
  }, [text, showRecipientName]);

  const colorPickerOptions = useMemo(() => [
  {
    visible: isColorPickerVisible,
    initialValue: style.color || '#1A1F24',
    style: {
      position: 'absolute',
      top: '40px',
      zIndex: '9',
      left: '-35px'
    }
  }],
  [isColorPickerVisible, style]);

  return (
    <RelativeBlock ref={componentRef}>
      {isEditable && canEdit ?
      <span className="edit-actions">
          {showRecipientName ?
        <EditibleBlock
          value={textValue}
          onChange={updateTitleText}
          setButtonRef={setButtonRef}
          {...titleProps}
          isVisible={isVisible} /> :


        <InputBase
          ref={(node) => setRef(node)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updateTitleText(e.target.value)}
          value={textValue}
          isVisible={isVisible}
          {...titleProps} />}


          <OptionsBlock
          colorPickerOptions={colorPickerOptions}
          handlePickerColorChange={handlePickerColorChange}
          isRecipientNameVisible={isRecipientNameVisible}
          addRecipientName={addRecipientName}
          options={options} />

        </span> :

      <ClickableDiv
        onClick={handleBlockClick}
        tabIndex="0"
        role="button"
        aria-pressed="false"
        onKeyDown={() => undefined}
        isVisible={isVisible}>

          {tag === 'h1' &&
        <TitleH1Base
          {...titleProps}
          dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />}


          {tag === 'h2' &&
        <TitleH2Base
          {...titleProps}
          dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />}


          {tag === 'h3' &&
        <TitleH3Base
          {...titleProps}
          dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />}


          {tag === 'h4' &&
        <TitleH4Base
          {...titleProps}
          dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />}


          {tag === 'h5' &&
        <TitleH5Base
          {...titleProps}
          dangerouslySetInnerHTML={{ __html: purifyHTML(replacedText) }} />}


        </ClickableDiv>}

    </RelativeBlock>);

};

export default memo(Title);